import {
  Box,
  Flex,
  getContrastColor,
  Group,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core"
import dayjs from "dayjs"
import { CalendarAppointment } from "../../../../../@client/Theasoft_ApiServer_Calendar"
import { ellipsify } from "../../../../../utils/ellipsify"
import { MIDDLE_OF_DAY } from "../SegmentCalendarAppointmentsToHalfDays"
import { RenderCalendarProps } from ".."
import { AbsenceIndicator } from "../absence-indicator"
import { useTranslations } from "../../../../../utils/use-t"
import { useTranslation } from "react-i18next"
import { timeOnlyToDayjs } from "../../../../../utils/time-only-to-dayjs"
import {
  useBackgroundColorForDate,
  useSchoolHolidayColor,
} from "../useColorForDate"

const calendarCellStyle: React.CSSProperties = {
  boxSizing: "border-box",
  flex: "1 0 100px",
  minWidth: "100px",
}

function AppointmentRenderer({
  appointment,
  hideTime,
  parentBgColor,
  showEndTime,
}: {
  parentBgColor?: string
  appointment: CalendarAppointment
  hideTime?: boolean
  showEndTime?: boolean
}) {
  const theme = useMantineTheme()
  return (
    <Text
      bg={
        appointment.backColorHEX ??
        parentBgColor ??
        (appointment.foreColorHEX
          ? getContrastColor({ color: appointment.foreColorHEX, theme })
          : "var(--mantine-color-body)")
      }
      c={
        appointment.foreColorHEX
          ? appointment.foreColorHEX
          : appointment.backColorHEX || parentBgColor
            ? getContrastColor({
                color: appointment.backColorHEX || parentBgColor,
                theme,
              })
            : undefined
      }
      px="0.2rem"
      w="fit-content"
      maw="100%"
      style={{
        ...ellipsify,
        borderRadius: 5,
        fontSize: "0.6rem",
        lineHeight: "1.2",
        position: "relative",
        zIndex: 98,
      }}
    >
      {`${!hideTime && appointment.start ? `${appointment.start.slice(0, 5)} ` : ""}${showEndTime && appointment.end ? `- ${appointment.end.slice(0, 5)} ` : ""}${appointment.source === "PossibleCast" ? "?" : ""}${appointment.type?.length ? `${appointment.type} ` : ""}`}
      <Text
        component="span"
        style={{ fontSize: "inherit", lineHeight: "inherit" }}
        fw={appointment.isPublicEvent ? 700 : undefined}
      >
        {appointment.title}
      </Text>
    </Text>
  )
}

function DayRenderer({
  isSchoolHoliday,
  date,
  appointments,
  status,
  parentBgColor,
  showEndTime,
}: {
  parentBgColor?: string
  appointments: CalendarAppointment[]
  status: CalendarAppointment[]
  isSchoolHoliday?: boolean
  date: Date
  showEndTime?: boolean
}) {
  const sortedEvents = appointments.reduce(
    (result, currentValue) => {
      if (
        currentValue.start &&
        timeOnlyToDayjs(currentValue.start).hour() < MIDDLE_OF_DAY
      ) {
        result.firstHalfOfDay.add(currentValue)
      } else if (currentValue.start) {
        result.secondHalfOfDay.add(currentValue)
      }

      if (!currentValue.start && !currentValue.end) {
        result.wholeDay.add(currentValue)
      }
      return result
    },
    {
      firstHalfOfDay: new Set<CalendarAppointment>(),
      secondHalfOfDay: new Set<CalendarAppointment>(),
      wholeDay: new Set<CalendarAppointment>(),
    }
  )

  const {
    appServer: { t },
  } = useTranslations()

  const theme = useMantineTheme()
  const schoolHolidayColor = useSchoolHolidayColor()

  const color =
    isSchoolHoliday || parentBgColor
      ? getContrastColor({
          color: isSchoolHoliday ? schoolHolidayColor : parentBgColor,
          theme,
        })
      : undefined

  const topRowAppointments = [
    ...sortedEvents.wholeDay,
    ...sortedEvents.firstHalfOfDay,
  ]

  return (
    <Flex h="100%" align="start">
      <Flex
        h="100%"
        direction="column"
        w="18px"
        maw="25px"
        pl="0.2rem"
        bg={isSchoolHoliday ? schoolHolidayColor : undefined}
      >
        <Text c={color} fw={600} pt="0.1rem" size="0.6rem">
          {dayjs(date).format("D")}
        </Text>
        <Text
          pt="1px"
          pb="0.1rem"
          size="0.6rem"
          c={parentBgColor || isSchoolHoliday ? color : "dimmed"}
        >
          {dayjs(date).format("dd")}
        </Text>
      </Flex>
      <Flex direction="column" w={0} flex="1 1 60%" px="0.2rem" pos="relative">
        <Box flex="1 1 50%">
          {topRowAppointments.map((appointment, i) => (
            <AppointmentRenderer
              parentBgColor={parentBgColor}
              key={i}
              appointment={appointment}
              showEndTime={showEndTime}
            />
          ))}
        </Box>
        <Box flex="1 1 50%">
          {[...sortedEvents.secondHalfOfDay].map((appointment, i) => (
            <AppointmentRenderer
              parentBgColor={parentBgColor}
              key={i}
              appointment={appointment}
              showEndTime={showEndTime}
            />
          ))}
        </Box>
        {date.getDay() === 1 ? (
          <Text
            pos="absolute"
            top={0}
            right={0}
            ta="right"
            pr="0.2rem"
            pt="0.1rem"
            size="0.5rem"
            c="dimmed"
          >
            {`${dayjs(date).isoWeek()} ${t("CW_CalendarWeek")}`}
          </Text>
        ) : null}
      </Flex>
      {status.length ? <AbsenceIndicator status={status} /> : null}
    </Flex>
  )
}

const DayTD = React.memo(function DayTD({
  day,
  date,
  isHoliday,
  isSchoolHoliday,
  events,
  status,
  showEndTime,
}: {
  events: CalendarAppointment[]
  status: CalendarAppointment[]
  isHoliday?: boolean
  isSchoolHoliday?: boolean
  day: number
  date: Date
  showEndTime?: boolean
}) {
  const bgColor = useBackgroundColorForDate(date, isHoliday)

  return (
    <Table.Td
      bg={bgColor}
      key={`day-${day}`}
      p="0"
      m="0"
      style={calendarCellStyle}
    >
      <DayRenderer
        parentBgColor={bgColor}
        isSchoolHoliday={isSchoolHoliday}
        date={date}
        appointments={events}
        status={status}
        showEndTime={showEndTime}
      />
    </Table.Td>
  )
})

export const CalendarTable = React.memo(
  ({
    startDate,
    endDate,
    eventsByDate,
    holidaysByDate,
    schoolHolidaysByDate,
    statusByDate,
    showEndTime,
  }: RenderCalendarProps) => {
    const totalMonths = dayjs(endDate).diff(dayjs(startDate), "month") + 1

    const columns: React.ReactElement[] = []
    const rows: React.ReactElement[] = []

    const eventCounts: Map<number, number> = new Map()

    useTranslation()

    for (let day = 1; day <= 31; day++) {
      const row = Array.from({ length: totalMonths }).map((_, monthIndex) => {
        const currentMonth = dayjs(startDate).add(monthIndex, "month")
        const date = new Date(currentMonth.year(), currentMonth.month(), day)
        const dayJsDate = dayjs(date).format("YYYY-MM-DD")

        const eventsForDate = eventsByDate.get(dayJsDate) || []
        const statusForDate = statusByDate.get(dayJsDate) || []
        const isHoliday = holidaysByDate.has(dayJsDate)
        const isSchoolHoliday = schoolHolidaysByDate.has(dayJsDate)

        eventsForDate.forEach((event) => {
          const currentCount =
            (eventCounts.get(monthIndex) ?? 0) + event.countingValue
          eventCounts.set(monthIndex, currentCount)
        })

        if (day === 31) {
          let totalCount = 0

          for (let index = 0; index <= monthIndex; index++) {
            totalCount = totalCount + (eventCounts.get(index) ?? 0)
          }
          columns.push(
            <Table.Th
              py="0.1rem"
              px="0.3rem"
              key={`month-${monthIndex}`}
              style={calendarCellStyle}
            >
              <Group
                gap={0}
                justify="space-between"
                align="baseline"
                wrap="nowrap"
              >
                <Text fw={600} size="sm" flex="1 1 10%" style={ellipsify}>
                  {currentMonth.format(
                    `MMM${monthIndex === 0 || currentMonth.month() === 0 ? " YYYY" : "M"}`
                  )}
                </Text>
                {[...eventCounts].some((x) => x[1] > 0) ? (
                  <Text size="xs">
                    {eventCounts.get(monthIndex) ?? 0}/{totalCount}
                  </Text>
                ) : null}
              </Group>
            </Table.Th>
          )
        }

        if (
          day > currentMonth.daysInMonth() ||
          date < startDate ||
          date > endDate
        ) {
          return (
            <Table.Td
              key={`empty-${day}-${monthIndex}`}
              style={calendarCellStyle}
            />
          )
        }

        return (
          <DayTD
            key={`day-${day}-${monthIndex}`}
            isHoliday={isHoliday}
            events={eventsForDate}
            status={statusForDate}
            isSchoolHoliday={isSchoolHoliday}
            day={day}
            date={date}
            showEndTime={showEndTime}
          />
        )
      })
      rows.push(
        <Table.Tr key={`row-${day}`} style={{ display: "flex" }}>
          {row}
        </Table.Tr>
      )
    }

    return (
      <Table withColumnBorders stickyHeader miw={totalMonths * 100}>
        <Table.Thead
          style={{ zIndex: 99, backgroundColor: "var(--mantine-color-body)" }}
        >
          <Table.Tr style={{ display: "flex" }}>{columns}</Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    )
  }
)
